import React from 'react';
import {Map} from 'immutable';
import connectToStores from 'alt-utils/lib/connectToStores';
import CoachingAgenda from 'components/dashboard/CoachingAgenda';
import UpcomingReviewsStore from 'stores/coaching/UpcomingReviewsStore';
import AuthStore from 'stores/AuthStore';
import partitionBy from 'utils/partitionBy';
import UpcomingReviewsActions from 'actions/coaching/UpcomingReviewsActions';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import CoachingPlanStore from 'stores/coaching/CoachingPlanStore';
import CoachingPlanActions from 'actions/coaching/CoachingPlanActions';
import {
  getCoachingRecommendations,
  widgetId as coachingRecommendations,
} from 'components/dashboard/manager/ManagerRecommendedCoaching';
import ManagerDashboardStore from 'stores/ManagerDashboardStore';
import CoachingReviewTypeStore, {typesAndCategories} from 'stores/coaching/CoachingReviewTypeStore';
import InProgressReviewStore from 'stores/coaching/InProgressReviewStore';
import {shouldUpdate} from 'components/StatsPage2.utils';
import UPCOMING_REVIEW_ROLES from 'actions/coaching/UPCOMING_REVIEW_ROLES';

@connectToStores
export default class CoachingAgendaContainer extends React.PureComponent {
  static getStores() {
    return [
      UpcomingReviewsStore,
      AuthStore,
      CoachingPlanStore,
      ManagerDashboardStore,
      CoachingReviewTypeStore,
      InProgressReviewStore,
    ];
  }

  static getPropsFromStores({managerId, selectedTeam}) {
    const dashStore = ManagerDashboardStore.getState();
    const planStore = CoachingPlanStore.getState();
    const reviewStore = UpcomingReviewsStore.getState();
    const sessionUser = AuthStore.getState().user;
    const userId = managerId ? managerId : sessionUser.id;

    const noPreviousPlans = !planStore.lastCompletePlan.get(userId);
    const planMap = planStore.planMap;

    const reviews = reviewStore.upcomingReviewsMap
      .get(userId, Map())
      .toList()
      .filter(review => {
        const teamId =
          // according to the Kotlin side these are the things that can be null
          review.coachingPlan &&
          review.coachingPlan.recipient.info &&
          review.coachingPlan.recipient.info.teamId;
        return !selectedTeam || !selectedTeam.id || selectedTeam.id === teamId;
      });

    const [readyReviews, notReady] = partitionBy(
      reviews,
      r => r.todo.done && r.otherParticipantTodo.done
    );

    //
    // Add in-progress ad-hoc reviews to readyReviews so they
    // are shown here as a way to get back into them.
    //
    const inProgressReviews = InProgressReviewStore.getState().inProgressReviews;

    const inProgressAdHocReviews = inProgressReviews
      .filter(x => x.adHoc)
      .valueSeq()
      .map(x => x.set('coachingPlan', planMap.get(x.planId)))

      // Sometimes completed reviews unexpectedly stay in local storage, but
      // their plan will be marked completed, and this will ensure that we
      // do not show them in the coaching agenda.
      .filter(x => !x.coachingPlan?.completed);

    const readyReviewsWithAdHoc = readyReviews
      .filter(x => !x.coachingPlan?.adHoc)
      .push(...inProgressAdHocReviews);

    const inProgressAdHocPlanIds = inProgressReviews
      .filter(x => x.adHoc)
      .valueSeq()
      .map(x => x.planId);

    const [coachReviews, coachReady] = partitionBy(notReady, r => !r.todo.done);
    const repReviews = coachReady.filter(r => !r.otherParticipantTodo.done);

    return {
      planMap,
      recommendationsWidget: dashStore.widgets.get(coachingRecommendations, {}),
      sessionUser,
      readyReviews: readyReviewsWithAdHoc,
      repReviews,
      coachReviews,
      noPreviousPlans,
      lastCompletePlanLoading: planStore.lastCompletePlanLoading,
      upcomingReviewsLoading: reviewStore.upcomingReviewsLoading,
      reviewTypes: typesAndCategories(CoachingReviewTypeStore)[1],
      inProgressAdHocPlanIds,
    };
  }

  constructor(props) {
    super(props);

    this.onSetReviewType = this.onSetReviewType.bind(this);
  }

  componentDidMount() {
    this.getData();
    this.getReviewData(this.props.readyReviews);
  }

  componentDidUpdate(prevProps) {
    if (shouldUpdate(prevProps, this.props, true, false, true)) {
      this.getData();
    }
    const {readyReviews} = this.props;
    if (readyReviews && !readyReviews.equals(prevProps.readyReviews)) {
      this.getReviewData(readyReviews);
    }
  }

  getData() {
    const {selectedTeam, managerId} = this.props;
    const userId = managerId ? managerId : AuthStore.getState().user.id;
    const teamId = selectedTeam ? selectedTeam.id : null;

    // we need plan information too - but assume this was loaded by ManagerSummaryDashboard/AdminSummaryDashboard
    CoachingPlanActions.fetchLastCompleteAndOverdue.defer([userId]);
    UpcomingReviewsActions.upcomingReviews.defer(
      userId,
      UPCOMING_REVIEW_ROLES.COACH,
      undefined,
      undefined,
      null /* reviewId */,
      teamId
    );
    // XXX Could cause problems if we use this is many places as it overwrites the state
    // of previous calls
    getCoachingRecommendations(coachingRecommendations, userId, selectedTeam, 3);
  }

  getReviewData(readyReviews) {
    readyReviews &&
      readyReviews.forEach(review => CoachingReviewActions.fetchReviewFeedback.defer(review.id));
  }

  onSetReviewType(review, reviewTypeId) {
    CoachingReviewActions.updateReview(review.set('reviewTypeId', reviewTypeId)).then(() =>
      this.getData()
    );
  }

  render() {
    const {planMap, managerId, recommendationsWidget, isNewAccount, inProgressAdHocPlanIds} =
      this.props;

    if (isNewAccount) {
      return null;
    }

    const userId = managerId ? managerId : AuthStore.getState().user.id;

    const plans = planMap
      .toList()
      // plans the user is a coach on
      .filter(p => p.coach.id === userId)
      // filter out invalid/error adhoc / livecoaching plans
      .filter(p => {
        // Keep ad-hoc plans that are in progress since we want to show
        // ad-hoc reviews in the coaching agenda as a way to return to them.
        if (inProgressAdHocPlanIds.contains(p.id)) {
          return true;
        }

        // Otherwise remove invalid/error ad-hoc and livecoaching plans.
        // (Original logic)
        return !(
          p.unscheduled() &&
          (!p.completed || !p.reviews.first() || !p.reviews.first().completedAt)
        );
      })
      // also - we don't care about completed long term plans here.
      .filter(p => !(!p.unscheduled() && p.completed));

    const completedLongTermPlanCount = planMap.filter(
      p => p.coach.id === userId && !p.adHoc && p.completed
    ).size;

    const adHocOnly =
      !plans.isEmpty() &&
      plans.every(plan => plan.unscheduled()) &&
      completedLongTermPlanCount === 0;

    const recs = recommendationsWidget.recommendations;
    const noRecommendations = recs && recs.length === 0;

    return (
      <CoachingAgenda
        {...this.props}
        plans={plans}
        adHocOnly={adHocOnly}
        noRecommendations={noRecommendations}
        refreshCoachingData={() => this.getData()}
        onSetReviewType={this.onSetReviewType}
      />
    );
  }
}
